import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { toastMessageError } from "../../utilities/commonToastMessage";

import {
  emptyLocalStorageOnLogout,
  trakingCodeGenrator,
} from "../../../util/helper";
import { currentUser } from "../../../services/auth";
import storage from "../../../util/storage";
import {
  connectSocketForConversionSetup,
  disconnectSocket,
  disconnectSocketForConversionSetup,
  socketForConversionSetup,
} from "../../../util/socket";

import ROUTES from "../../../constants/routes";
import { DEFAULT_VALUE, UseFulLinks } from "../../../constants/commonConstants";

import ActionType from "../../../resources/enums";

import Header from "../../header/Header";
import InfoModel from "../../commonModel/InfoModel";
import CommonModel from "../../commonModel/CommonModel";

import ClipIcon from "../../../assets/images/clippy.svg";
import "./ConversionSetup.scss";

const ConversionSetup = () => {
  const [windowInstance, setWindowInstance] = useState<boolean>(false);
  const authReducer: any = useSelector((state: any) => state?.AuthReducer);
  const dispatch = useDispatch();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const siteId = authReducer.authData.site._id;
  const socketOptions = {
    query: `siteId=${siteId}&role=admin&roomStatus=conversion-setup`,
  };
  const [showCongratsModel, setShowCongratsModel] = useState<boolean>(false);
  const [showFailModel, setShowFailModel] = useState<boolean>(false);
  const [deleteAcountModel, setDeleteAcountModel] = useState<boolean>(false);
  // The one-time "Welcome to Skydive" ghost-bubble preview modal that used to open here was REMOVED on 2026-10-20 (Chris: "It is of NO value"). Its component, its animation service
  // and the client call that marked it seen are gone; the backend flag and route are untouched.
  const navigate = useNavigate();

  useEffect(() => {
    if (
      window.location.pathname.split("/")[
        window.location.pathname.split("/").length - 1
      ] === "conversion-setup" &&
      authReducer.authData.flags.trackingCodeInstalled
    ) {
      return navigate(ROUTES.SIGNUP);
    }
    connectSocketForConversionSetup(socketOptions);
    getTrackingCode(siteId);

    /*
     * 🔴 RE-ASK THE SERVER ON MOUNT (added 2026-09-10).
     *
     * This screen decided whether tracking was installed from
     * `authReducer.authData.flags`, which is REDUX - and redux-persist writes
     * it to localStorage. So a refresh rehydrated the same stale `false` and
     * could never recover.
     *
     * Detection otherwise relied entirely on a LIVE socket event. If the
     * customer's first visitor arrived while they were not sitting on this
     * exact page - or the socket blipped - nothing ever told this screen, and
     * refreshing could not help. The only escape was logging out and back in,
     * which nobody would think to do: the app simply looks stuck on "install
     * your code" after you have already installed it.
     *
     * Hit in real use: a genuine tracked visit set the flag server-side, the
     * database said `trackingCodeInstalled: true`, and the screen stayed put
     * across repeated refreshes.
     *
     * One request on mount closes it. The socket path is untouched and still
     * handles the common case where the visit arrives while they watch.
     */
    (async () => {
      try {
        const fresh: any = await currentUser();
        if (fresh?.data?.flags?.trackingCodeInstalled) {
          dispatch({ type: ActionType.LOGIN, payload: fresh.data });
          if (fresh.data.site?._id) {
            storage.set("siteId", fresh.data.site._id);
          }
          navigate(
            ROUTES.ANALYTICS_VIEW.replace(":id", fresh.data.site?._id || "")
          );
        }
      } catch (e) {
        // Leave the screen as it is and let the socket path do its job - a
        // failed check must never block someone who genuinely has not
        // installed the code yet.
      }
    })();
  }, []);

  const { t: translation } = useTranslation();
  const [trackingCode, setTrackingCode] = useState("");
  const getTrackingCode = (siteId: string) => {
    const trackingCodeData = trakingCodeGenrator(siteId);
    setTrackingCode(trackingCodeData);
  };

  let siteWindow: any;
  // @ts-ignore
  let siteConnected = false;
  const verifyTrackingCode = () => {
    try {
      siteWindow = window.open(
        authReducer.authData.site.siteUrl + "?verifySkydive=true",
        "_blank"
      );
      socketForConversionSetup.on("site-info", (payload: any) => {
        setWindowInstance(true);

        siteConnected = true;
        if (
          payload &&
          payload.message &&
          payload.message === "Site Connected Successfully"
        ) {
          setShowCongratsModel(true);
          siteWindow.close();
          clearTimeout(timer);
          disconnectSocketForConversionSetup();
          return;
        } else {
          socketConnectionCheck();
        }
        siteWindow.close();
      });
      if (!siteConnected) {
        socketConnectionCheck();
      }
    } catch (err) {
      /*
       * Was an empty catch. This runs the "check my site is connected" flow,
       * so a failure here leaves the user waiting on a check that has already
       * given up - the single most confusing place to stay quiet. Same
       * reasoning as PaymentMethod.tsx: generic message to the user, detail
       * to the console.
       */
      console.error("Conversion setup check failed", err);
      toastMessageError(translation("something_gone_wrong_please_try_again"));
    }
  };
  const handleCopy = () => {
    if (textAreaRef.current) {
      textAreaRef.current.select();

      document.execCommand("copy");
    }
  };

  let timer;
  const socketConnectionCheck = () => {
    timer = setTimeout(() => {
      socketForConversionSetup.disconnect();
      toastMessageError(
        translation("verification_time_out"),
        "verificationTimeOutError"
      );

      siteWindow.close();
      disconnectSocketForConversionSetup();
    }, DEFAULT_VALUE.SETUP_TIMEOUT);
  };

  return (
    <>
      {/* Real bug reported + fixed 2026-08-14: this page's URL
          (/conversion-setup) has no site-id segment, but Header's
          Settings link derives its target by parsing the pathname's last
          segment assuming a site id always precedes it - here that
          assumption breaks and Settings navigates somewhere broken.
          hideNav also matches the actual product decision: these nav
          links (Playback/Settings/Help) don't belong on a page reachable
          before tracking verification is even complete. */}
      <Header hideNav />
      <div className="main-container setup-main">
        {showCongratsModel && (
          <InfoModel
            closeModel={() => {
              setShowCongratsModel(false);
              
              navigate(
                window.location.pathname.split("/")[
                  window.location.pathname.split("/").length - 1
                ] === "conversion-setup"
                  ? ROUTES.CONVERSION_SETUP.replace("/:id", "")
                  : ROUTES.CONVERSION_SETUP.replace(
                      ":id",
                      window.location.pathname.split("/")[
                        window.location.pathname.split("/").length - 1
                      ]
                    )
              );
            }}
            name="success"
          />
        )}
        {showFailModel && (
          <InfoModel closeModel={() => setShowFailModel(false)} name="fail" />
        )}
        <div className="container">
          <h1>
            {translation("install_your_tracking_code_on")}{" "}
            <strong>{authReducer.authData.site.host}</strong>
          </h1>

          <p>{translation("installing_tracking_code_description")}</p>
          <div className="form-group textarea-group copy-icon">
            <textarea
              ref={textAreaRef}
              rows={8}
              placeholder={translation("place_your_script_here")}
              className="form-control"
              defaultValue={trackingCode}
              readOnly
            />
            <span className="copy-icon" onClick={handleCopy}>
              <img src={ClipIcon} alt="ClipIcon" />
            </span>
          </div>
          <div className="mt-4 d-flex align-items-center justify-content-between">
            <button
              className="theme-btn success-btn text-uppercase fontBold"
              onClick={() => {
                verifyTrackingCode();
              }}
            >
              {translation("verify_my_code_is_installed")}
            </button>
            <button
              className="theme-btn white-btn text-uppercase fontBold"
              onClick={() => {
                dispatch({ type: ActionType.LOGOUT, payload: "" });
                disconnectSocket();
                emptyLocalStorageOnLogout();
              }}
            >
              {translation("log_off_complete_later")}
            </button>
          </div>
          <div className="installing-text">
            <p>
              {translation("for_help_with_installing_your_code")}{" "}
              <Link to={UseFulLinks.installingSkydive} target="_blank">
                <strong>{translation("installing_skydive")}</strong>
              </Link>{" "}
              {translation("or")} &nbsp;
              <Link to={UseFulLinks.contactUs} target="_blank">
                <strong>{translation("contact_us")}</strong>
              </Link>
            </p>

            <p>
              {translation("made_a_mistake_with_signing_up_with_this_account")}{" "}
              <Link onClick={() => setDeleteAcountModel(true)} to={""}>
                <strong>{translation("delete_your_account")}</strong>
              </Link>
            </p>
          </div>
        </div>
      </div>
      {deleteAcountModel && (
        <CommonModel
          title={"Delete Your Account"}
          onClose={() => {
            setDeleteAcountModel(false);
          }}
          name="DeleteAccount"
        />
      )}
    </>
  );
};

export default ConversionSetup;
